import { ArrowLeft, CheckCircle2, CreditCard, Globe2, LayoutDashboard, Loader2, MessageCircleMore, Package, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Brand } from "../components/ui/Brand";

type UpgradeData = {
  upgrade: { targetPlan: "PRO" | "EXPORT"; status: string; expiresAt: string };
  targetPlanLabel: string;
  account: { displayName: string; whatsapp: string; planLabel: string; site: { slug: string; businessName: string } | null };
};

export function UpgradePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<UpgradeData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetch(`/api/upgrade/${token}`).then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "UPGRADE_LINK_INVALID");
      setData(body);
    }).catch(() => setError("Tautan upgrade tidak valid atau sudah kedaluwarsa.")).finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setProcessing(true);
    setError("");
    try {
      const response = await fetch(`/api/upgrade/${token}`, { method: "POST" });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "UPGRADE_FAILED");
      const nextUrl = body.activationUrl || body.dashboardUrl || "/login";
      navigate(new URL(nextUrl, window.location.origin).pathname);
    } catch {
      setError("Upgrade belum berhasil diproses. Silakan ulangi dari WhatsApp.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_15%_10%,_#dcfce7,_transparent_32%),radial-gradient(circle_at_85%_90%,_#ffedd5,_transparent_30%),#f8fafc] px-5 py-8 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between"><Brand /><Link to="/" className="inline-flex items-center gap-2 text-sm font-extrabold text-slate-500 hover:text-brand-700"><ArrowLeft size={16} /> Landing page</Link></div>
        <div className="mt-10 overflow-hidden rounded-[2.25rem] border border-white bg-white/90 shadow-soft backdrop-blur-xl">
          {loading ? <div className="grid min-h-[520px] place-items-center"><Loader2 className="animate-spin text-brand-600" size={34} /></div> : error && !data ? <div className="grid min-h-[520px] place-items-center p-8 text-center"><div><div className="text-6xl">⚠️</div><h1 className="mt-4 text-3xl font-black text-slate-900">Tautan tidak tersedia</h1><p className="mt-3 text-slate-500">{error}</p></div></div> : data && (
            <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
              <section className="p-7 sm:p-10 lg:p-12">
                <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-brand-700"><Sparkles size={14} /> Upgrade seamless</span>
                <h1 className="mt-6 text-4xl font-black tracking-[-0.045em] text-slate-900 sm:text-5xl">Buka {data.targetPlanLabel}, tanpa membuat website baru.</h1>
                <p className="mt-5 max-w-xl text-base leading-7 text-slate-500">Website <strong className="text-slate-800">{data.account.site?.businessName}</strong> tetap memakai slug, produk, QRIS, nomor pemesanan, dan riwayat yang sama. Upgrade hanya membuka entitlement dashboard.</p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {[
                    [Globe2, "URL tetap", data.account.site?.slug || "Slug website lama"],
                    [Package, "Data tetap", "Produk, QRIS, dan pesanan"],
                    [MessageCircleMore, "WhatsApp tetap aktif", "Edit cepat masih bisa lewat chat"],
                    [LayoutDashboard, "Dashboard terbuka", "Builder, produk, order, analytics"],
                  ].map(([Icon, title, copy]) => {
                    const ItemIcon = Icon as typeof Globe2;
                    return <div key={title as string} className="rounded-2xl border border-slate-200 p-4"><ItemIcon size={20} className="text-brand-700" /><p className="mt-3 text-sm font-black text-slate-800">{title as string}</p><p className="mt-1 text-xs leading-5 text-slate-500">{copy as string}</p></div>;
                  })}
                </div>
              </section>

              <aside className="bg-gradient-to-br from-brand-700 via-brand-800 to-brand-950 p-7 text-white sm:p-10 lg:p-12">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-brand-200">Ringkasan upgrade</p>
                <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.07] p-5">
                  <p className="text-sm text-white/60">Nomor akun</p><p className="mt-1 text-lg font-black">{data.account.whatsapp}</p>
                  <div className="my-5 border-t border-white/10" />
                  <p className="text-sm text-white/60">Paket saat ini</p><p className="mt-1 font-extrabold">{data.account.planLabel}</p>
                  <p className="mt-4 text-sm text-white/60">Paket tujuan</p><p className="mt-1 text-2xl font-black text-accent-300">{data.targetPlanLabel}</p>
                </div>
                <div className="mt-6 rounded-3xl bg-white p-5 text-slate-900">
                  <div className="flex items-center gap-3"><CreditCard className="text-accent-600" /><div><p className="text-sm font-black">Pembayaran simulasi lokal</p><p className="text-xs text-slate-500">Tidak ada transaksi dana nyata.</p></div></div>
                  <button type="button" onClick={confirm} disabled={processing} className="btn-accent mt-5 w-full !py-3.5">{processing ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}{processing ? "Memproses..." : "Simulasikan pembayaran berhasil"}</button>
                </div>
                {error && <div className="mt-4 rounded-2xl bg-rose-500/15 p-3 text-sm font-bold text-rose-100">{error}</div>}
              </aside>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
